/**
 * Kalkulationsgrundlagen des Betriebs (Lohngruppen, Zuschläge, Geräte) und der
 * Weg Projektkalkulation → Angebotspositionen.
 *
 * Gerechnet wird ausschließlich mit @optero/kalkulation-core. Jede Position
 * trägt ihren Rechenweg als Snapshot (kalkulation) mit ins Dokument.
 */

import {
    d, resolve, lohnkostensatz, berechnePosition, KOSTENARTEN, gliedern,
} from '@optero/kalkulation-core';
import { supabase } from './supabaseClient';
import { belegDatum, aktuelleWerte, juengstesDatum } from './belegZeit';

let cache = null;

const heute = () => new Date().toISOString().slice(0, 10);

export async function ladeGrundlagen({ neu = false } = {}) {
    if (cache && !neu) return cache;
    await supabase.rpc('kalkulation_sicherstellen');
    const [lg, saetze, zu, ger] = await Promise.all([
        supabase.from('lohngruppen').select('*').eq('aktiv', true).order('sort_index'),
        supabase.from('lohngruppen_saetze').select('*').order('gueltig_ab'),
        supabase.from('zuschlaege').select('*').order('gueltig_ab'),
        supabase.from('geraete').select('*').eq('aktiv', true).order('name'),
    ]);
    for (const r of [lg, saetze, zu, ger]) if (r.error) throw new Error(r.error.message);

    const lohngruppen = (lg.data || []).map((g) => ({
        ...g,
        saetze: (saetze.data || []).filter((s) => s.lohngruppe_id === g.id),
    }));
    cache = { lohngruppen, zuschlaege: zu.data || [], geraete: ger.data || [] };
    return cache;
}

export function grundlagenVerwerfen() { cache = null; }

/** Satz einer Lohngruppe zum Stichtag: { lks_h, vs_h, zuschlag_prozent, version, rechenweg } */
export function satzFuer(grundlagen, lohngruppeId, stichtag = heute()) {
    const gruppe = grundlagen.lohngruppen.find((g) => g.id === lohngruppeId)
        || grundlagen.lohngruppen.find((g) => g.ist_standard)
        || grundlagen.lohngruppen[0];
    if (!gruppe || gruppe.saetze.length === 0) return null;
    const version = resolve(gruppe.saetze, stichtag);
    const s = lohnkostensatz(version);
    return { gruppe, version, ...s };
}

/** Zuschlagsprofil zum Stichtag: { MATERIAL: 20, … } */
export function zuschlagsprofil(grundlagen, stichtag = heute()) {
    const profil = {};
    for (const ka of KOSTENARTEN) {
        const versionen = grundlagen.zuschlaege.filter((z) => z.kostenart === ka);
        if (versionen.length) {
            try { profil[ka] = Number(resolve(versionen, stichtag).prozent); } catch { /* noch nicht gültig */ }
        }
    }
    return profil;
}

/** VK aus EK für eine Materialzeile — Zuschlag der Zeile, sonst Profil. */
export function vkAusEk(ek, zuschlagProzent) {
    return d(ek).mal(d(1).plus(d(zuschlagProzent || 0).durch(100))).runde(2).alsZahl(2);
}

/** Zuschlagsprofil aus dem geladenen Cache — leer, solange noch nichts geladen ist. */
export function zuschlagsprofilAusCache() {
    return cache ? zuschlagsprofil(cache) : {};
}

const zahlOderNull = (x) => (x === null || x === undefined || x === '' ? null : Number(x));

/**
 * Materialzeile: VK, EK und Zuschlag passen immer zusammen. Gleiche Regel wie
 * der DB-Trigger material_preis_ableiten (20261026100):
 *   • EK oder Zuschlag geändert → VK = EK × (1 + Zuschlag)
 *   • nur VK geändert           → Zuschlag = VK ÷ EK − 1, der Preis bleibt wie eingegeben
 *   • neu ohne Zuschlag und VK  → Zuschlag aus dem Profil
 * Ohne EK ist der VK ein Festpreis. `alt` = gespeicherter Stand (null bei neu).
 */
export function materialPreiseAngleichen(neu, alt = null, profil = {}) {
    const ek = zahlOderNull(neu.ek_einzelpreis);
    if (ek === null) return neu;
    const z = zahlOderNull(neu.zuschlag_prozent);
    const vk = zahlOderNull(neu.einzelpreis);
    const basisGeaendert = alt
        ? zahlOderNull(alt.ek_einzelpreis) !== ek || zahlOderNull(alt.zuschlag_prozent) !== z
        : z !== null || vk === null;
    const nurVkGeaendert = !basisGeaendert && (!alt || vk !== zahlOderNull(alt.einzelpreis));

    if (nurVkGeaendert) {
        if (ek > 0 && vk !== null) {
            return { ...neu, zuschlag_prozent: d(vk).durch(ek).minus(1).mal(100).runde(4).alsZahl(4) };
        }
        return neu;
    }
    if (basisGeaendert) {
        const zuschlag = z ?? profil[neu.kostenart || 'MATERIAL'] ?? 0;
        return { ...neu, zuschlag_prozent: zuschlag, einzelpreis: vkAusEk(ek, zuschlag) };
    }
    return neu;
}

/** Bestandteile mit aktuellen Sätzen auflösen (Lohn über die Lohngruppe, Gerät über den Gerätestamm). */
export function bestandteileAufloesen(bestandteile, grundlagen, stichtag = heute()) {
    const profil = zuschlagsprofil(grundlagen, stichtag);
    return (bestandteile || []).map((b) => {
        if (b.kostenart === 'LOHN') {
            const satz = satzFuer(grundlagen, b.lohngruppe_id, stichtag);
            return {
                ...b,
                einheit: b.einheit || 'min',
                ek_je_einheit: satz ? satz.lks_h.alsZahl(4) : 0,
                zuschlag_prozent: satz ? satz.zuschlag_prozent.alsZahl(4) : 0,
                zuschlag_quelle: satz ? `Lohngruppe ${satz.gruppe.name}` : 'fehlt',
                beschreibung: b.beschreibung || satz?.gruppe?.name || 'Lohn',
            };
        }
        if (b.kostenart === 'GERAET' && b.geraet_id) {
            const g = grundlagen.geraete.find((x) => x.id === b.geraet_id);
            return { ...b, ek_je_einheit: g ? Number(g.kostensatz_h) : (b.ek_je_einheit || 0), zuschlag_prozent: b.zuschlag_prozent ?? profil.GERAET ?? 0 };
        }
        return { ...b, zuschlag_prozent: b.zuschlag_prozent ?? profil[b.kostenart] ?? 0 };
    });
}

const jsonZahl = (x, stellen = 4) => (x === null || x === undefined ? null : d(x).alsZahl(stellen));

function snapshot(ergebnis, extra = {}) {
    const je = (o) => Object.fromEntries(Object.entries(o || {}).map(([k, v]) => [k, jsonZahl(v)]));
    return {
        modus: ergebnis.modus,
        ek_je_einheit: je(ergebnis.ek_je_einheit),
        vk_je_einheit: je(ergebnis.vk_je_einheit),
        ek_summe_je_einheit: jsonZahl(ergebnis.ek_summe_je_einheit),
        vk_summe_je_einheit: jsonZahl(ergebnis.vk_summe_je_einheit),
        arbeitskosten_je_einheit: jsonZahl(ergebnis.arbeitskosten_je_einheit),
        zeitbedarf_min: jsonZahl(ergebnis.zeitbedarf_min, 3),
        mischzuschlag_prozent: jsonZahl(ergebnis.mischzuschlag_prozent, 2),
        bestandteile: ergebnis.bestandteile || [],
        rechenweg: ergebnis.rechenweg,
        ...extra,
    };
}

/** Katalogleistung mit Festpreis je Einheit (z. B. Parkett 87,50 €/m²) → EP, sonst null */
export function festpreisJeEinheit(task) {
    const k = task?.kalkulation;
    return k && !k.kalkulation_aktiv && k.vk_fest !== null && k.vk_fest !== undefined ? Number(k.vk_fest) : null;
}

/**
 * Arbeit (project_task) → Position. Mit Bezugsmenge (z. B. 100 m²) im
 * Gesamtpreis-Modus: die Stunden gelten für die ganze Menge, der EP ist €/m².
 */
export function positionAusArbeit(task, { grundlagen, stichtag, projektSatz }) {
    const satz = satzFuer(grundlagen, task.lohngruppe_id, stichtag);
    const stunden = Number(task.estimated_hours || 0);
    const hatMenge = Number(task.menge_plan) > 0 && (task.menge_unit || task.einheit_plan);
    const menge = hatMenge ? Number(task.menge_plan) : 1;
    const einheit = hatMenge ? (task.menge_unit || task.einheit_plan) : 'psch';

    // Leistung aus dem Katalog: Festpreis je Einheit …
    const k = task.kalkulation;
    if (festpreisJeEinheit(task) !== null) {
        const ergebnis = berechnePosition({ kalkulation_aktiv: false, vk_fest: k.vk_fest, menge });
        return {
            beschreibung: task.name, langtext: task.description || '', menge, einheit,
            einzelpreis: ergebnis.ep.alsZahl(2), gesamtpreis: ergebnis.gp.alsZahl(2),
            position_typ: task.position_typ || 'standard',
            kalkulation: snapshot(ergebnis, { stunden, leistung: k.leistung || task.name }),
            herkunft: { tabelle: 'project_tasks', id: task.id, leistung_id: task.leistung_id || null },
        };
    }
    // … oder kalkuliert: Material/Gerät je Einheit × Menge, Lohn = geplante Stunden
    if (k?.kalkulation_aktiv && (k.bestandteile || []).length) {
        const aufgeloest = bestandteileAufloesen(k.bestandteile, grundlagen, stichtag);
        const lohn = aufgeloest.find((b) => b.kostenart === 'LOHN');
        const bestandteile = [
            ...aufgeloest.filter((b) => b.kostenart !== 'LOHN').map((b) => ({ ...b, menge: Number(b.menge || 0) * menge })),
            ...(stunden > 0 ? [{ ...(lohn || { kostenart: 'LOHN', beschreibung: satz?.gruppe?.name || 'Lohn', ek_je_einheit: satz ? satz.lks_h.alsZahl(4) : 0, zuschlag_prozent: satz ? satz.zuschlag_prozent.alsZahl(4) : 0 }), menge: stunden, einheit: 'h' }] : []),
        ];
        const ergebnis = berechnePosition({ modus: 'GESAMTPREIS', menge, bestandteile });
        return {
            beschreibung: task.name, langtext: task.description || '', menge, einheit,
            einzelpreis: ergebnis.ep.alsZahl(2), gesamtpreis: ergebnis.gp.alsZahl(2),
            position_typ: task.position_typ || 'standard',
            kalkulation: snapshot(ergebnis, { stunden, leistung: k.leistung || task.name }),
            herkunft: { tabelle: 'project_tasks', id: task.id, leistung_id: task.leistung_id || null },
        };
    }

    // Projekt-Stundensatz (Pauschalvereinbarung mit dem Kunden) übersteuert die Lohngruppe
    const bestandteil = projektSatz > 0
        ? { kostenart: 'LOHN', beschreibung: 'Projektstundensatz', menge: stunden, einheit: 'h', ek_je_einheit: projektSatz, zuschlag_prozent: 0, zuschlag_quelle: 'Projekt' }
        : {
            kostenart: 'LOHN',
            beschreibung: satz?.gruppe?.name || 'Lohn',
            menge: stunden,
            einheit: 'h',
            ek_je_einheit: satz ? satz.lks_h.alsZahl(4) : 0,
            zuschlag_prozent: satz ? satz.zuschlag_prozent.alsZahl(4) : 0,
            zuschlag_quelle: satz ? `Lohngruppe ${satz.gruppe.name}` : 'fehlt',
        };

    const ergebnis = berechnePosition({ modus: 'GESAMTPREIS', menge, bestandteile: [bestandteil] });
    return {
        beschreibung: task.name,
        langtext: task.description || '',
        menge,
        einheit,
        einzelpreis: ergebnis.ep.alsZahl(2),
        gesamtpreis: ergebnis.gp.alsZahl(2),
        position_typ: task.position_typ || 'standard',
        kalkulation: snapshot(ergebnis, {
            stunden,
            lohngruppe: satz?.gruppe?.name || null,
            verrechnungssatz: projektSatz > 0 ? projektSatz : (satz ? satz.vs_h.alsZahl(2) : null),
        }),
        herkunft: { tabelle: 'project_tasks', id: task.id },
    };
}

/** Material → Position. Mit EK: EK × (1 + Zuschlag); sonst Festpreis (einzelpreis = VK). */
export function positionAusMaterial(m, { profil }) {
    const menge = Number(m.menge ?? 1);
    const kostenart = m.kostenart || 'MATERIAL';
    let ergebnis;
    if (m.ek_einzelpreis !== null && m.ek_einzelpreis !== undefined) {
        const z = m.zuschlag_prozent ?? profil[kostenart] ?? 0;
        ergebnis = berechnePosition({
            modus: 'EINHEITSPREIS',
            menge,
            bestandteile: [{
                kostenart, beschreibung: m.name, menge: 1, einheit: m.einheit, ek_je_einheit: m.ek_einzelpreis,
                zuschlag_prozent: z, zuschlag_quelle: m.zuschlag_prozent !== null && m.zuschlag_prozent !== undefined ? 'Position' : 'Zuschlagsprofil',
            }],
        });
    } else {
        ergebnis = berechnePosition({ kalkulation_aktiv: false, vk_fest: m.einzelpreis || 0, menge });
    }
    return {
        beschreibung: m.name,
        menge,
        einheit: m.einheit || 'Stk',
        einzelpreis: ergebnis.ep.alsZahl(2),
        gesamtpreis: ergebnis.gp.alsZahl(2),
        position_typ: m.position_typ || 'standard',
        kalkulation: snapshot(ergebnis, { preis_unbekannt: !(Number(m.einzelpreis) > 0) && m.ek_einzelpreis == null }),
        ...(m.shop_sku ? { shop_sku: m.shop_sku } : {}),
        herkunft: { tabelle: 'posten_material', id: m.id },
    };
}

/** Rechenkontext eines Projekts: Grundlagen, Zuschlagsprofil, ggf. vereinbarter Projekt-Stundensatz. */
export function preisKontext(grundlagen, projekt, stichtag = heute()) {
    return {
        grundlagen,
        stichtag,
        profil: zuschlagsprofil(grundlagen, stichtag),
        projektSatz: Number(projekt?.project_hourly_rate || projekt?.hourly_rate || 0),
    };
}

/**
 * Preise einer Projektkalkulation — exakt so, wie das Angebot sie rechnet.
 * Übersicht, Rechenweg und Angebot dürfen keine eigene Rechnung haben.
 */
export function preiseFuerProjekt(grundlagen, projekt, stichtag = heute()) {
    if (!grundlagen) return null;
    const ctx = preisKontext(grundlagen, projekt, stichtag);
    return {
        arbeit: (t) => positionAusArbeit(t, ctx),
        material: (m) => positionAusMaterial(m, ctx),
        /** Verrechnungssatz, mit dem diese Arbeit gerechnet wird (null = Lohngruppe ohne Satz) */
        satz: (t) => (ctx.projektSatz > 0
            ? ctx.projektSatz
            : satzFuer(grundlagen, t?.lohngruppe_id, stichtag)?.vs_h.alsZahl(2) ?? null),
    };
}

/**
 * Projektkalkulation (Posten, Arbeiten, Material) → Angebotsgruppen.
 * Reihenfolge: Leistungsbereiche, dann Material, Nebenleistungen zuletzt.
 * Jede Position kennt beide Gliederungen (Posten = LB, betriebs_gruppe = Stil
 * des Betriebs); `gliederung` wählt, welche das Dokument zeigt.
 */
export async function angebotsGruppenAusKalkulation({
    posten, tasks, materials, projekt, stichtag = heute(), sortiereMaterial, gliederung = 'lb', gruppenTexte = {},
}) {
    const grundlagen = await ladeGrundlagen();
    const ctx = preisKontext(grundlagen, projekt, stichtag);
    const { profil } = ctx;
    // Arbeit zählt, wenn sie Stunden hat oder aus dem Leistungskatalog stammt (Festpreis)
    const preisrelevant = (t) => Number(t.estimated_hours || 0) > 0 || !!t.kalkulation;

    const sortiert = [...posten].sort((a, b) => {
        if (a.name === 'Nebenleistungen') return 1;
        if (b.name === 'Nebenleistungen') return -1;
        return (a.sort_index || 0) - (b.sort_index || 0);
    });

    const gruppen = [];
    const fehlerhaft = [];
    for (const p of sortiert) {
        const pTasks = tasks.filter((t) => t.posten_id === p.id && preisrelevant(t));
        const pMat = materials.filter((m) => m.posten_id === p.id);
        if (pTasks.length === 0 && pMat.length === 0) continue;
        const zuordnung = (quelle) => ({ lb: p.name, betrieb: quelle.betriebs_gruppe || null });
        const positionen = [
            ...pTasks.map((t) => ({ ...positionAusArbeit(t, ctx), gliederung: zuordnung(t) })),
            ...(sortiereMaterial ? sortiereMaterial(pMat) : pMat).map((m) => ({ ...positionAusMaterial(m, ctx), gliederung: zuordnung(m) })),
        ];
        positionen.forEach((pos) => {
            if (pos.kalkulation?.bestandteile?.some((b) => b.zuschlag_quelle === 'fehlt') || pos.kalkulation?.preis_unbekannt) {
                fehlerhaft.push(pos.beschreibung);
            }
        });
        gruppen.push({ name: p.name, positionen });
    }

    const ohnePosten = tasks.filter((t) => !t.posten_id && preisrelevant(t));
    if (ohnePosten.length) {
        gruppen.push({
            name: 'Sonstige Leistungen',
            positionen: ohnePosten.map((t) => ({ ...positionAusArbeit(t, ctx), gliederung: { lb: 'Sonstige Leistungen', betrieb: t.betriebs_gruppe || null } })),
        });
    }

    // Ursprüngliche Reihenfolge merken — daran richtet sich jede Umgliederung aus
    let i = 0;
    gruppen.forEach((g) => g.positionen.forEach((pos) => { pos.gliederung.reihenfolge = i++; }));
    // Positionsnummern: Gruppe.Position (gliedern() nummeriert selbst)
    gruppen.forEach((g, gi) => g.positionen.forEach((pos, pi) => { pos.nr = `${gi + 1}.${pi + 1}`; }));
    const ergebnis = gliederung === 'betrieb' ? gliedern(gruppen, { von: 'lb', nach: 'betrieb', texte: gruppenTexte }) : gruppen;
    return { gruppen: ergebnis, fehlerhaft, profil };
}

// ─── Speichern ──────────────────────────────────────────────────────────────

/** Neuer Satz für eine Lohngruppe. Gleiches gueltig_ab = Korrektur, sonst neue Version. */
export async function speichereLohnsatz(lohngruppeId, satz) {
    const zeile = {
        lohngruppe_id: lohngruppeId,
        gueltig_ab: satz.gueltig_ab || heute(),
        eingabe: satz.eingabe,
        verrechnungssatz: satz.eingabe === 'direkt' ? satz.verrechnungssatz : null,
        lohnkostensatz: satz.eingabe === 'direkt' ? (satz.lohnkostensatz ?? null) : null,
        stundenlohn: satz.eingabe === 'herleitung' ? satz.stundenlohn : null,
        lohnnebenkosten: satz.lohnnebenkosten ?? null,
        lohnnebenkosten_modus: satz.lohnnebenkosten_modus || 'prozent',
        gemeinkosten: satz.gemeinkosten ?? null,
        gemeinkosten_modus: satz.gemeinkosten_modus || 'euro',
        zuschlag_prozent: satz.zuschlag_prozent ?? null,
        quelle: satz.quelle || 'manuell',
        beleg: satz.beleg || null,
        ...(satz.kalkulation_id ? { kalkulation_id: satz.kalkulation_id } : {}),
    };
    const { error } = await supabase.from('lohngruppen_saetze').upsert(zeile, { onConflict: 'lohngruppe_id,gueltig_ab' });
    if (error) throw new Error(error.message);
    grundlagenVerwerfen();
}

export async function legeLohngruppeAn(name) {
    const { data, error } = await supabase.from('lohngruppen').insert({ name, sort_index: 99 }).select('*').single();
    if (error) throw new Error(error.message);
    grundlagenVerwerfen();
    return data;
}

export async function setzeStandardLohngruppe(id) {
    await supabase.from('lohngruppen').update({ ist_standard: false }).eq('ist_standard', true);
    const { error } = await supabase.from('lohngruppen').update({ ist_standard: true }).eq('id', id);
    if (error) throw new Error(error.message);
    grundlagenVerwerfen();
}

export async function deaktiviereLohngruppe(id) {
    const { error } = await supabase.from('lohngruppen').update({ aktiv: false, ist_standard: false }).eq('id', id);
    if (error) throw new Error(error.message);
    grundlagenVerwerfen();
}

export async function speichereZuschlag(kostenart, prozent, gueltigAb = heute()) {
    const { error } = await supabase.from('zuschlaege')
        .upsert({ kostenart, prozent, gueltig_ab: gueltigAb, quelle: 'manuell' }, { onConflict: 'betrieb_id,kostenart,gueltig_ab' });
    if (error) throw new Error(error.message);
    grundlagenVerwerfen();
}

export async function speichereGeraet(geraet) {
    const { id, ...felder } = geraet;
    const q = id
        ? supabase.from('geraete').update(felder).eq('id', id)
        : supabase.from('geraete').insert(felder);
    const { error } = await q;
    if (error) throw new Error(error.message);
    grundlagenVerwerfen();
}

// ─── HeyOptero: Stundensätze aus Altbelegen ─────────────────────────────────

const LOHN_RE = /(arbeitsstunde|arbeitszeit|stunde|std\.?|regie|lohn|monteur|geselle|meister|helfer|azubi|lehrling|facharbeiter)/i;
const ZEIT_EINHEIT_RE = /^(h|std\.?|stunde[n]?|std)$/i;

export function rolleAus(text) {
    const t = text.toLowerCase();
    if (/meister/.test(t)) return 'Meister';
    if (/helfer|hilfskraft/.test(t)) return 'Helfer';
    if (/azubi|lehrling|auszubild/.test(t)) return 'Azubi';
    if (/geselle|facharbeiter|monteur|tischler|schreiner/.test(t)) return 'Geselle';
    return null;
}

/**
 * Sucht in den hochgeladenen Altbelegen (Onboarding) nach abgerechneten
 * Stunden und schlägt daraus Verrechnungssätze je Lohngruppe vor. Rechnungen
 * zählen mehr als Angebote — dort steht, was tatsächlich bezahlt wurde.
 */
export async function stundensatzVorschlaege() {
    const { data, error } = await supabase.from('onboarding_documents').select('id, file_name, doc_type, extracted');
    if (error) return [];
    const funde = {};
    const stichtag = juengstesDatum((data || []).map((d) => belegDatum(d.extracted?.document?.datum)));
    for (const doc of data || []) {
        const datum = belegDatum(doc.extracted?.document?.datum);
        const positionen = doc.extracted?.positions || doc.extracted?.positionen || [];
        const istRechnung = /rechnung/i.test(doc.doc_type || doc.extracted?.document?.typ || doc.file_name || '');
        for (const p of positionen) {
            const text = `${p.bezeichnung || ''} ${p.beschreibung || ''}`;
            const ep = Number(p.einzelpreis);
            // Nur echte Stundenpositionen: Zeiteinheit UND Lohnbegriff, plausibler Satz
            if (!(ep > 15 && ep < 200)) continue;
            if (!ZEIT_EINHEIT_RE.test(String(p.einheit || '').trim()) || !LOHN_RE.test(text)) continue;
            const rolle = rolleAus(text) || 'Geselle';
            (funde[rolle] ||= []).push({ ep, wert: ep, datum, gewicht: istRechnung ? 2 : 1, beleg: doc.file_name, text: text.trim() });
        }
    }
    return Object.entries(funde).map(([rolle, alle]) => {
        // Sätze steigen über die Jahre — maßgeblich sind die letzten 12 Monate
        const liste = aktuelleWerte(alle, stichtag);
        // Gewichteter Median: robust gegen einzelne Sonderpreise
        const sortiert = liste.flatMap((f) => Array(f.gewicht).fill(f.ep)).sort((a, b) => a - b);
        const median = sortiert[Math.floor(sortiert.length / 2)];
        return {
            rolle,
            verrechnungssatz: Math.round(median * 100) / 100,
            anzahl: liste.length,
            spanne: [Math.min(...liste.map((f) => f.ep)), Math.max(...liste.map((f) => f.ep))],
            belege: [...new Set(liste.map((f) => f.beleg))].slice(0, 5),
            beispiele: liste.slice(0, 3).map((f) => f.text),
        };
    });
}

// ─── Redux-Spiegel ──────────────────────────────────────────────────────────

/** Heutige Sätze aller Lohngruppen für Redux (Plantafel, HeyOptero, Zeiten). */
export function lohnsaetzeHeute(grundlagen) {
    const lohngruppen = grundlagen.lohngruppen.map((g) => {
        const s = satzFuer(grundlagen, g.id);
        return {
            id: g.id,
            name: g.name,
            ist_standard: g.ist_standard,
            vs: s ? s.vs_h.alsZahl(2) : null,
            lks: s ? s.lks_h.alsZahl(2) : null,
        };
    }).filter((g) => g.vs !== null);
    return { lohngruppen, standard: lohngruppen.find((g) => g.ist_standard) || lohngruppen[0] || null };
}

/** Grundlagen neu laden und die heutigen Sätze nach Redux spiegeln. */
export async function lohnsaetzeAktualisieren() {
    const { default: store } = await import('../app/store');
    const { setLohnsaetze } = await import('../features/settings/settingsSlice');
    const g = await ladeGrundlagen({ neu: true });
    store.dispatch(setLohnsaetze(lohnsaetzeHeute(g)));
    return g;
}
